import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useBKOAcao } from '@/hooks/useBKOAcao';
import { usePedidoMovement } from '@/hooks/usePedidoMovement';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { aplicarEscopoTriagem } from '@/components/kanban/triagem/triagemPedidoUtils';
import { useSmartPolling } from '@/hooks/useSmartPolling';
import { POLLING_INTERVALS } from '@/lib/performanceConfig';
import { posicaoNaFila } from '@/lib/filaTriagem';
import { manterPedidoNoFim } from '@/components/kanban/triagem/manterPedidoNoFim';
import { depoisDoClique } from '@/components/kanban/triagem/acaoTriagem';

export interface ItemTriagemBiometria {
  item_id: string;
  produto: string;
  quantidade: number;
  plano_valor: number;
  subtotal: number;
}

/** As duas flags do cadastro que decidem o que é faturamento e o que é aparelho. */
interface ProdutoFlagsFaturamento {
  nome: string;
  fat_total: boolean | null;
  fat_aparelhos: boolean | null;
}

export interface PedidoTriagemBiometria {
  pedido_id: string;
  numero_pedido: string;
  cnpj: string;
  /** CNPJ ou CPF — a fila mostra um segmento por vez (`segmentoTriagem.ts`). */
  tipo_pessoa: string | null;
  /** CNPJ ou CPF, como está no banco. Máscara em `formatarDocumento`. */
  documento: string;
  razao_social: string;
  consultor_nome: string;
  equipe_nome: string;
  entrada_coluna_em: string;
  /**
   * FATURAMENTO do pedido — só produtos com o KPI `FAT. TOTAL` ligado.
   * ⛔ APARELHO não entra aqui: ele tem KPI próprio e é contado à parte, em
   * `valor_aparelhos`. Douglas 17/08/2026: "não é junto, é separado". Somando
   * junto, dois aparelhos (R$ 16.398,00) viravam 56% do total da fila da Bio e
   * a tela dizia que a biometria segurava faturamento que ela não segura.
   */
  valor_total: number;
  /** Aparelhos do pedido, em R$ — mostrado separado, nunca somado ao faturamento. */
  valor_aparelhos: number;
  /** Quantos aparelhos o pedido leva (a pill da fila fala em quantidade). */
  qtd_aparelhos: number;
  lead_origem_id: string | null;
  telefone: string | null;
  /** Todos os telefones do GESTOR — o cliente costuma responder de um número diferente do cadastrado */
  telefones: string[];
  /** Nome do gestor: é ele quem faz a biometria, então é o nome que a tela mostra */
  gestor_nome: string | null;
  /** Quantas ligações e mensagens saíram para o gestor DESDE que o pedido caiu na coluna */
  ligacoes: number;
  whats: number;
  /** Sinalização do comercial: "já cobrei / o cliente já fez" — só marca, não move o pedido */
  sinalizado_em: string | null;
  sinalizado_por: string | null;
  /** 'feito' = 🖐 do comercial · 'nao_feito' = 👎 do BKO depois de conferir */
  sinal_estado: 'feito' | 'nao_feito' | null;
  /** Recado do BKO ao consultor quando marca como não feito */
  sinal_observacao: string | null;
  /** Passado para o fim da fila com justificativa (padrão Manter). */
  manter_em: string | null;
  /** Quem pegou o pedido lá no Pedido Fechado — segue dono até o fim */
  dono_id: string | null;
  dono_nome: string;
  consultor_id: string | null;
  cliente_id: string | null;
  itens: ItemTriagemBiometria[];
}

/**
 * O lado COMERCIAL só sinaliza na biometria. Devolver p/ Criação, mandar p/
 * Aceite e Cancelar é do BKO — Douglas 05/08: "quem realmente finaliza, cancela
 * ou volta é sempre o BKO".
 */
const ROLES_SO_SINALIZAM = ['consultor', 'supervisor', 'coordenador'];

export function useTriagemBiometria() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { executarAcaoConsolidadaAsync } = useBKOAcao();
  const { moverPedidoJunto } = usePedidoMovement();
  const { profile } = useAuth();
  const soSinaliza = ROLES_SO_SINALIZAM.includes(String(profile?.role || '').toLowerCase());
  const { refetchInterval, onQueryError, onQuerySuccess } = useSmartPolling({
    weight: 'B',
    baseInterval: POLLING_INTERVALS.PESO_B,
  });

  const { data: pedidos = [], isLoading, refetch } = useQuery({
    queryKey: ['triagem-biometria', profile?.id],
    queryFn: async () => {
      // Régua única do faturamento: o KPI `FAT. TOTAL` do cadastro. `FAT.
      // APARELHOS` vem junto para o aparelho ser exibido à parte em vez de sumir.
      const { data: prods } = await supabase
        .from('produtos')
        .select('nome, fat_total, fat_aparelhos');
      const nomeNorm = (n: unknown) => String(n || '').toUpperCase().trim();
      const flags = (prods || []) as ProdutoFlagsFaturamento[];
      const ehFaturamento = new Set(flags.filter((p) => p.fat_total).map((p) => nomeNorm(p.nome)));
      const ehAparelho = new Set(flags.filter((p) => p.fat_aparelhos).map((p) => nomeNorm(p.nome)));

      let q = supabase
        .from('v_planilha_mae')
        .select('item_id, pedido_id, numero_pedido, cnpj, razao_social, consultor_nome, consultor_id, equipe_nome, produto, quantidade, plano_valor, subtotal, entrada_coluna_em, lead_origem_id, gestor_nome, gestor_telefone, gestor_whatsapp, contato_real_nome, contato_real_telefone, bko_id, bko_atual_nome, cliente_id, tipo_pessoa, documento, manter_em')
        .eq('coluna_atual', 'BIOMETRIA')
        .eq('quadro_atual', 'CONTRATO_PRIME')
        .order('entrada_coluna_em', { ascending: true })
        .order('pedido_id', { ascending: true });
      // Escopo por papel — mesma régua do Kanban (BKO vê só as equipes mapeadas)
      q = await aplicarEscopoTriagem(q, profile);
      const { data, error } = await q;

      if (error) {
        onQueryError(error);
        throw error;
      }
      onQuerySuccess();

      const porPedido = new Map<string, PedidoTriagemBiometria>();
      for (const item of (data || []) as any[]) {
        const subtotal = Number(item.subtotal) || (Number(item.plano_valor) || 0) * (Number(item.quantidade) || 1);
        let pedido = porPedido.get(item.pedido_id);
        if (!pedido) {
          pedido = {
            pedido_id: item.pedido_id,
            numero_pedido: String(item.numero_pedido || ''),
            cnpj: item.cnpj || '',
            tipo_pessoa: item.tipo_pessoa ?? null,
            documento: item.documento || item.cnpj || '',
            razao_social: item.razao_social || '',
            consultor_nome: item.consultor_nome || '',
            equipe_nome: item.equipe_nome || '',
            entrada_coluna_em: item.entrada_coluna_em || '',
            valor_total: 0,
            valor_aparelhos: 0,
            qtd_aparelhos: 0,
            lead_origem_id: item.lead_origem_id || null,
            // ⛔ O telefone da biometria é o DO GESTOR — quem assina e faz a
            // validação biométrica. Douglas 06/08: "tem que ser do gestor".
            // Lê `gestor_telefone` pelo nome de verdade e NÃO cai para
            // telefone_principal / cliente_whatsapp: aqueles são da empresa, e
            // ligar para eles é cobrar biometria de quem não vai fazer.
            // (`contato_real_telefone` da v_planilha_mae é o mesmo
            // gestor_telefone com outro rótulo — COALESCE(cg.gestor_telefone).)
            telefone: item.gestor_telefone || item.gestor_whatsapp || item.contato_real_telefone || null,
            telefones: Array.from(new Set(
              [item.gestor_telefone, item.gestor_whatsapp, item.contato_real_telefone]
                .map((t: unknown) => String(t || '').trim())
                .filter((t: string) => t.length > 0),
            )),
            gestor_nome: item.gestor_nome || item.contato_real_nome || null,
            ligacoes: 0,
            whats: 0,
            sinalizado_em: null,
            sinalizado_por: null,
            sinal_estado: null,
            sinal_observacao: null,
            manter_em: null,
            dono_id: item.bko_id || null,
            dono_nome: item.bko_atual_nome || '',
            consultor_id: item.consultor_id || null,
            cliente_id: item.cliente_id || null,
            itens: [],
          };
          porPedido.set(item.pedido_id, pedido);
        }
        if (item.entrada_coluna_em && (!pedido.entrada_coluna_em || item.entrada_coluna_em < pedido.entrada_coluna_em)) {
          pedido.entrada_coluna_em = item.entrada_coluna_em;
        }
        if (item.manter_em && (!pedido.manter_em || item.manter_em > pedido.manter_em)) {
          pedido.manter_em = item.manter_em;
        }
        const produtoNorm = nomeNorm(item.produto);
        if (ehAparelho.has(produtoNorm)) {
          pedido.valor_aparelhos += subtotal;
          pedido.qtd_aparelhos += Number(item.quantidade) || 1;
        }
        else if (ehFaturamento.has(produtoNorm)) pedido.valor_total += subtotal;
        pedido.itens.push({
          item_id: item.item_id,
          produto: item.produto || '',
          quantidade: Number(item.quantidade) || 0,
          plano_valor: Number(item.plano_valor) || 0,
          subtotal,
        });
      }

      // Sinalização do comercial ("já cobrei / o cliente já fez"). Só vale para a
      // passagem ATUAL pela coluna: marca anterior ao entrada_coluna_em é de uma
      // ida-e-volta antiga (voltou p/ Criação e retornou) e não conta.
      const ids = Array.from(porPedido.keys());
      for (let i = 0; i < ids.length; i += 100) {
        const lote = ids.slice(i, i + 100);
        const { data: marcas } = await supabase
          .from('biometria_sinalizacoes')
          // `*` de propósito: `estado` e `observacao` nasceram na migration de
          // 19/08 e ainda não estão no mapa gerado em `integrations/supabase/
          // types.ts` — listar o nome quebraria o typecheck. A tabela tem 6
          // colunas, então `*` não pesa.
          .select('*')
          .in('pedido_id', lote);
        for (const marca of marcas || []) {
          const pedido = porPedido.get(marca.pedido_id);
          if (!pedido || !marca.marcado_em) continue;
          const entrada = pedido.entrada_coluna_em ? new Date(pedido.entrada_coluna_em).getTime() : 0;
          if (new Date(marca.marcado_em).getTime() < entrada) continue;
          pedido.sinalizado_em = marca.marcado_em;
          pedido.sinalizado_por = marca.usuario_nome || null;
          pedido.sinal_estado = ((marca as { estado?: string }).estado as 'feito' | 'nao_feito') || 'feito';
          pedido.sinal_observacao = (marca as { observacao?: string }).observacao || null;
        }
      }

      // Quantas vezes cada cliente ja' foi cobrado DESDE que caiu na coluna.
      // Zera sozinho quando o pedido sai e volta: `entrada_coluna_em` e' reescrito
      // a cada mudanca de coluna, e e' ele o recorte da contagem.
      const alvos = Array.from(porPedido.values())
        .map((p) => {
          const d = String(p.telefone || '').replace(/\D/g, '');
          return { pedido: p, suf: d.length >= 8 ? d.slice(-8) : '', desde: p.entrada_coluna_em };
        })
        .filter((a) => a.suf && a.desde);

      if (alvos.length > 0) {
        const { data: contagens } = await supabase.rpc('crm_biometria_contatos', {
          p_pedidos: alvos.map((a) => ({ suf: a.suf, desde: a.desde })),
        });
        const porSufixo = new Map<string, { ligacoes: number; whats: number }>();
        for (const linha of (contagens || []) as { suf: string; ligacoes: number; whats: number }[]) {
          porSufixo.set(linha.suf, { ligacoes: linha.ligacoes ?? 0, whats: linha.whats ?? 0 });
        }
        for (const a of alvos) {
          const c = porSufixo.get(a.suf);
          if (c) { a.pedido.ligacoes = c.ligacoes; a.pedido.whats = c.whats; }
        }
      }

      // Fila FIFO: pedido mais antigo na coluna primeiro; o adiado pelo Manter
      // conta da hora em que foi adiado (vai para o fim).
      return Array.from(porPedido.values()).sort((a, b) =>
        posicaoNaFila(a.entrada_coluna_em, a.manter_em) - posicaoNaFila(b.entrada_coluna_em, b.manter_em)
      );
    },
    refetchInterval,
  });

  const invalidateAllQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['triagem-biometria'] });
    queryClient.invalidateQueries({ queryKey: ['triagem-criacao-pedido'] });
    queryClient.invalidateQueries({ queryKey: ['kanban-optimized'] });
    queryClient.invalidateQueries({ queryKey: ['kanban'] });
    queryClient.invalidateQueries({ queryKey: ['planilha-itens'] });
    queryClient.invalidateQueries({ queryKey: ['v_planilha_mae'] });
    queryClient.invalidateQueries({ queryKey: ['timeline-unificada'] });
  };

  // A linha sai (ou desce) assim que o banco confirma; a Criação, vizinha que
  // recebe o devolvido, se relê junto.
  const depoisDeAndar = (pedidoId: string, modo: 'sai' | 'desce') => {
    void queryClient.invalidateQueries({ queryKey: ['timeline-unificada'] });
    return depoisDoClique(queryClient, ['triagem-biometria', 'triagem-criacao-pedido'], { pedidoId }, modo);
  };

  // Biometria concluída → ACEITE (motor consolidado do BKO: histórico +
  // entrada_coluna_em + aviso automático ao consultor ao entrar no ACEITE)
  const enviarParaAceite = useMutation({
    mutationFn: async (pedido: PedidoTriagemBiometria) => {
      await executarAcaoConsolidadaAsync({
        itensIds: pedido.itens.map((i) => i.item_id),
        pedidoId: pedido.pedido_id,
        colunaAtual: 'BIOMETRIA',
        origemTriagem: true,
      });
      return { pedidoId: pedido.pedido_id, numeroPedido: pedido.numero_pedido, itens: pedido.itens.length };
    },
    onSuccess: ({ pedidoId, numeroPedido, itens }) => {
      toast({
        title: 'Biometria concluída',
        description: `Pedido ${numeroPedido} (${itens} ite${itens !== 1 ? 'ns' : 'm'}) enviado para ACEITE.`,
      });
      void depoisDeAndar(pedidoId, 'sai');
    },
    onError: (error) => {
      toast({
        title: 'Erro ao enviar para aceite',
        description: error instanceof Error ? error.message : 'Tente novamente.',
        variant: 'destructive',
      });
    },
  });

  // Link expirado / problema na criação → devolve o pedido inteiro p/ CRIAÇÃO PEDIDO
  const voltarParaCriacao = useMutation({
    mutationFn: async (pedido: PedidoTriagemBiometria) => {
      await moverPedidoJunto({
        pedidoId: pedido.pedido_id,
        itensIds: pedido.itens.map((i) => i.item_id),
        quadroOrigem: 'CONTRATO_PRIME',
        colunaOrigem: 'BIOMETRIA',
        toQuadro: 'CONTRATO_PRIME',
        toColuna: 'CRIAÇÃO PEDIDO',
        motivo: 'Triagem Biometria — devolvido para Criação Pedido',
        origemTriagem: true,
      });
      return { pedidoId: pedido.pedido_id, numeroPedido: pedido.numero_pedido, itens: pedido.itens.length };
    },
    onSuccess: ({ pedidoId, numeroPedido, itens }) => {
      toast({
        title: 'Devolvido para Criação',
        description: `Pedido ${numeroPedido} (${itens} ite${itens !== 1 ? 'ns' : 'm'}) voltou para CRIAÇÃO PEDIDO.`,
      });
      void depoisDeAndar(pedidoId, 'sai');
    },
    onError: (error) => {
      toast({
        title: 'Erro ao devolver para criação',
        description: error instanceof Error ? error.message : 'Tente novamente.',
        variant: 'destructive',
      });
    },
  });

  // Sinalização do comercial: avisa o BKO que já cobrou / o cliente já fez a
  // biometria. NÃO muda coluna, NÃO tira da fila, NÃO abre aviso do sistema —
  // é só o marcador que o BKO confere na própria tela da triagem.
  const alternarSinalizacao = useMutation({
    mutationFn: async (pedido: PedidoTriagemBiometria) => {
      const jaSinalizado = !!pedido.sinalizado_em;
      if (jaSinalizado) {
        const { error } = await supabase
          .from('biometria_sinalizacoes')
          .delete()
          .eq('pedido_id', pedido.pedido_id);
        if (error) throw error;
      } else {
        if (!profile?.id) throw new Error('Sessão sem usuário — recarregue a página.');
        const { error } = await supabase
          .from('biometria_sinalizacoes')
          .upsert(
            {
              pedido_id: pedido.pedido_id,
              usuario_id: profile.id,
              usuario_nome: profile.nome || null,
              marcado_em: new Date().toISOString(),
              estado: 'feito',
              observacao: null,
            },
            { onConflict: 'pedido_id' },
          );
        if (error) throw error;
      }
      return { numeroPedido: pedido.numero_pedido, jaSinalizado };
    },
    onSuccess: ({ numeroPedido, jaSinalizado }) => {
      toast({
        title: jaSinalizado ? 'Sinalização removida' : '🙋 BKO avisado',
        description: jaSinalizado
          ? `Pedido ${numeroPedido} voltou a aparecer sem sinalização.`
          : `Pedido ${numeroPedido} marcado como feito — o pedido continua na fila até o BKO conferir.`,
        duration: 2500,
      });
      queryClient.invalidateQueries({ queryKey: ['triagem-biometria'] });
    },
    onError: (error) => {
      toast({
        title: 'Erro ao sinalizar',
        description: error instanceof Error ? error.message : 'Tente novamente.',
        variant: 'destructive',
      });
    },
  });

  // 👎 do BKO: conferiu e a biometria NÃO foi feita. Derruba o 🖐 do comercial
  // (que virou "eu consultei", não "está pronto"), deixa o alerta de pendente na
  // linha e leva o recado ao consultor — na timeline e na notificação dele.
  // Clicar de novo no 👎 limpa a marca e o pedido volta a ficar sem sinal.
  const manterNoFim = useMutation({
    mutationFn: async ({ pedido, justificativa, prd }: { pedido: PedidoTriagemBiometria; justificativa: string; prd?: string }) => {
      const r = await manterPedidoNoFim({ titulo: 'Triagem Biometria', pedido, justificativa, prd, coluna: 'BIOMETRIA' });
      return { ...r, pedidoId: pedido.pedido_id };
    },
    onSuccess: ({ pedidoId, numeroPedido }) => {
      toast({
        title: 'Passado para o fim da fila',
        description: `Pedido ${numeroPedido} adiado com justificativa.`,
      });
      void depoisDeAndar(pedidoId, 'desce');
    },
    onError: (error) => {
      toast({
        title: 'Erro ao passar para o fim da fila',
        description: error instanceof Error ? error.message : 'Tente novamente.',
        variant: 'destructive',
      });
    },
  });

  const marcarNaoFeito = useMutation({
    mutationFn: async ({ pedido, recado }: { pedido: PedidoTriagemBiometria; recado?: string }) => {
      if (!profile?.id) throw new Error('Sessão sem usuário — recarregue a página.');

      if (pedido.sinal_estado === 'nao_feito') {
        const { error } = await supabase
          .from('biometria_sinalizacoes')
          .delete()
          .eq('pedido_id', pedido.pedido_id);
        if (error) throw error;
        return { numeroPedido: pedido.numero_pedido, limpou: true };
      }

      const texto = (recado || '').trim();
      const { error } = await supabase
        .from('biometria_sinalizacoes')
        .upsert(
          {
            pedido_id: pedido.pedido_id,
            usuario_id: profile.id,
            usuario_nome: profile.nome || null,
            marcado_em: new Date().toISOString(),
            estado: 'nao_feito',
            observacao: texto || null,
          } as never,
          { onConflict: 'pedido_id' },
        );
      if (error) throw error;

      // Timeline do pedido (quem conferiu e o que disse) e o aviso ao consultor
      // (ele precisa saber sem abrir a triagem) saem juntos, um não espera o outro.
      const corpo = `👎 Triagem Biometria — BKO conferiu e a biometria NÃO foi feita${texto ? `: ${texto}` : '.'}`;
      await Promise.all([
        supabase.from('mensagem').insert({
          cliente_id: pedido.cliente_id,
          pedido_id: pedido.pedido_id,
          canal: 'interno',
          direcao: 'out',
          corpo,
          criado_por: profile.id,
          status: 'enviado',
        } as never),
        pedido.consultor_id
          ? supabase.from('notificacoes').insert({
            usuario_id: pedido.consultor_id,
            tipo: 'biometria_nao_feita',
            titulo: '👎 Biometria ainda NÃO foi feita',
            mensagem: `Pedido #${pedido.numero_pedido} — ${pedido.razao_social}. ${texto || 'O BKO conferiu e a biometria não saiu. Cobre o gestor.'}`,
            pedido_id: pedido.pedido_id,
            prioridade: 'alta',
          } as never)
          : Promise.resolve(),
      ]);

      return { numeroPedido: pedido.numero_pedido, limpou: false };
    },
    onSuccess: ({ numeroPedido, limpou }) => {
      toast({
        title: limpou ? 'Marca removida' : '👎 Marcado como NÃO feito',
        description: limpou
          ? `Pedido ${numeroPedido} voltou a ficar sem sinal.`
          : `Pedido ${numeroPedido}: o consultor foi avisado e o pedido segue na fila.`,
        duration: 2500,
      });
      queryClient.invalidateQueries({ queryKey: ['triagem-biometria'] });
    },
    onError: (error) => {
      toast({
        title: 'Não deu para marcar',
        description: error instanceof Error ? error.message : 'Atualize a tela e tente de novo.',
        variant: 'destructive',
      });
    },
  });

  return {
    pedidos,
    isLoading,
    refetch,
    invalidateAllQueries,
    enviarParaAceite: enviarParaAceite.mutateAsync,
    voltarParaCriacao: voltarParaCriacao.mutateAsync,
    alternarSinalizacao: alternarSinalizacao.mutateAsync,
    marcarNaoFeito: marcarNaoFeito.mutateAsync,
    manterNoFim: manterNoFim.mutateAsync,
    /** Comercial só trabalha e sinaliza; devolver/aceitar/cancelar é do BKO */
    podeAcoesBko: !soSinaliza,
    podeSinalizar: soSinaliza,
  };
}
